import { NavLink, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, useHasModule } from "../features/auth/AuthProvider.js";
import { FlashBanner } from "../components/FlashBanner.js";
import { ThemeSwitcher } from "../components/ui/ThemeSwitcher.js";
import { LocaleSwitcher } from "../components/ui/LocaleSwitcher.js";

const USERS_MANAGEMENT_ROLES = new Set(["superadmin", "platform-support", "tenant-admin"]);

export function PortalLayout(): JSX.Element {
  const { user, logout, flash, setFlash } = useAuth();
  const { t } = useTranslation();
  const hasInventory = useHasModule("inventory");
  const hasOrders = useHasModule("orders");
  const hasTraceability = useHasModule("core"); /* traceability forma parte del core */
  const canManageUsers = !!user && USERS_MANAGEMENT_ROLES.has(user.role);

  return (
    <div className="portal-shell">
      <aside className="portal-sidebar" aria-label="Navegación principal">
        <div className="portal-brand">
          <span className="portal-brand-title">0xvera Trace</span>
          <span className="portal-brand-sub">Portal</span>
        </div>
        <nav className="portal-nav">
          <NavLink to="/" end className={({ isActive }) => (isActive ? "portal-nav-link active" : "portal-nav-link")}>
            {t("nav.home")}
          </NavLink>
          <NavLink
            to="/analytics"
            className={({ isActive }) => (isActive ? "portal-nav-link active" : "portal-nav-link")}
          >
            {t("nav.analytics")}
          </NavLink>
          <NavLink to="/profile" className={({ isActive }) => (isActive ? "portal-nav-link active" : "portal-nav-link")}>
            {t("nav.profile")}
          </NavLink>
          {hasInventory && (
            <NavLink to="/inventory" className={({ isActive }) => (isActive ? "portal-nav-link active" : "portal-nav-link")}>
              {t("nav.inventory")}
            </NavLink>
          )}
          {hasInventory && (
            <NavLink
              to="/products"
              className={({ isActive }) => (isActive ? "portal-nav-link active" : "portal-nav-link")}
            >
              {t("nav.products")}
            </NavLink>
          )}
          {hasInventory && (
            <NavLink
              to="/warehouses"
              className={({ isActive }) => (isActive ? "portal-nav-link active" : "portal-nav-link")}
            >
              {t("nav.warehouses")}
            </NavLink>
          )}
          {hasOrders && (
            <NavLink to="/orders" className={({ isActive }) => (isActive ? "portal-nav-link active" : "portal-nav-link")}>
              {t("nav.orders")}
            </NavLink>
          )}
          {hasTraceability && (
            <NavLink to="/traceability" className={({ isActive }) => (isActive ? "portal-nav-link active" : "portal-nav-link")}>
              {t("nav.traceability")}
            </NavLink>
          )}
          {canManageUsers && (
            <NavLink
              to="/settings/users"
              className={({ isActive }) => (isActive ? "portal-nav-link active" : "portal-nav-link")}
            >
              {t("nav.users")}
            </NavLink>
          )}
        </nav>
      </aside>
      <div className="portal-main-wrap">
        <header className="portal-header">
          <div className="portal-header-user">
            <span className="portal-user-email" title={user?.email}>
              {user?.email}
            </span>
            <span className="portal-user-role">{user?.role}</span>
          </div>
          <div className="portal-header-controls">
            <LocaleSwitcher />
            <ThemeSwitcher />
            <button type="button" className="btn btn-ghost" onClick={() => logout()}>
              {t("nav.logout")}
            </button>
          </div>
        </header>
        {flash ? <FlashBanner message={flash} onDismiss={() => setFlash(null)} /> : null}
        <main className="portal-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
